import React, { useCallback, useState } from 'react'
import { Status, SidebarAgencyQuery, useSidebarAgencyLazyQuery } from '../../../__generated__/graphql'
import { SidebarHeader } from './SidebarHeader'
import { SidebarFooter } from './SidebarFooter'
import { DivMenuContent } from './SidebarBody'
import { useCompanyDetailsContext } from '../../../utils/hooks/useCompanyDetailsContext'
import SidebarSkeleton from '../../loader/SidebarSkeleton'
import { ListSidebarContent } from './Sidebar'
import { ListItemButton, ListItemText } from '@mui/material'
import { NavLink } from 'react-router-dom'
import { AdvertiserRouteBuilder } from '../../../pages/routeParams'
import { sidebarActiveItemSx } from './sidebarStyles'
import { fetchAllPages } from '../../../utils/fetchAllPages'
import { useFetchAllPages } from '../../../utils/hooks/useFetchAllPages'
import { useRegisterSidebarReload } from '../../../utils/providers/sidebarContext'
import { SIDEBAR_QUERY_PAGE_SIZE } from './sidebarPagination'

type AgencyAdvertiser = SidebarAgencyQuery['searchCompanies'][number]

export const AgencySidebarContent = (): JSX.Element => {
  const companyDetails = useCompanyDetailsContext()
  const [hasArchived, setHasArchived] = useState(false)
  const [loadQuery] = useSidebarAgencyLazyQuery({
    notifyOnNetworkStatusChange: true,
  })

  const loadAll = useCallback(async (): Promise<AgencyAdvertiser[]> => {
    const status = Object.values(Status).filter((status) => !hasArchived || status == Status.Archived)

    return fetchAllPages(async (pagination) => {
      const { data } = await loadQuery({
        variables: {
          agencyId: companyDetails.company.id,
          status,
          pagination,
        },
      })

      return data?.searchCompanies ?? []
    }, SIDEBAR_QUERY_PAGE_SIZE)
  }, [companyDetails.company.id, hasArchived, loadQuery])

  const { items: advertisers, loading, reload } = useFetchAllPages(loadAll)
  useRegisterSidebarReload(reload)

  return (
    <ListSidebarContent>
      <SidebarHeader updateWithArchived={(showArchived: boolean) => setHasArchived(showArchived)} items={[]} />
      {loading && advertisers.length === 0 ? (
        <SidebarSkeleton />
      ) : (
        <DivMenuContent data-testid="sidebarBody">
          {advertisers.map((advertiser) => (
            <ListItemButton
              key={advertiser.id}
              disableRipple
              component={NavLink}
              activeclassname="active"
              to={AdvertiserRouteBuilder(advertiser.slug)}
              sx={sidebarActiveItemSx}
            >
              <ListItemText>{advertiser.name}</ListItemText>
            </ListItemButton>
          ))}
        </DivMenuContent>
      )}
      <SidebarFooter />
    </ListSidebarContent>
  )
}

export default AgencySidebarContent
